import ArrowLeftIcon from '@heroicons/react/24/outline/ArrowLeftIcon';
import ArrowRightIcon from '@heroicons/react/24/outline/ArrowRightIcon';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Loader } from '@/components/core/Loader';
import { Uploader } from '@/components/core/Uploader';
import type { UploadItem } from '@/components/core/Uploader/types';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { useAssetCategories, useAssetOptionGroups } from '@/pages/dam/api';
import {
    DAM_UPLOAD_FORMAT_LABELS,
    type DamUploadFile,
    hasValidationErrors,
    hasValidationWarnings,
    isFailed,
    isIncomplete,
    isUploaded,
} from '@/pages/dam/utils/damUploadFiles';
import { useDamLeaveGuard } from '@/pages/dam/utils/useDamLeaveGuard';
import { useDamLinkedItemColumns } from '@/pages/dam/utils/useDamLinkedItemColumns';
import { type DamCreateResult, useDamUpload } from '@/pages/dam/utils/useDamUpload';
import type { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import { DamImageLightbox } from '../DamImageLightbox';
import { DamUploadDrawer } from '../DamUploadDrawer';
import { DamUploadFileList } from '../DamUploadFileList';
import { DamUploadSummary } from '../DamUploadSummary';
import { DamUploadTabs } from '../DamUploadTabs';

const DAM_UPLOAD_LAYOUT = 'asset_upload';

type DamUploadStep = 1 | 2 | 3 | 4;
type DamUploadTab = 'all' | 'failed' | 'noProduct' | 'incomplete' | 'warnings' | 'errors';

interface DamUploadWizardProps {
    onClose: () => void;
    /** Called after "Create" when at least one asset was created. */
    onCreated: (result: DamCreateResult) => void;
}

const STEP_TITLE_KEYS: Record<DamUploadStep, string> = {
    1: 'DAM.UPLOAD.STEP_UPLOAD',
    2: 'DAM.UPLOAD.STEP_LINK',
    3: 'DAM.UPLOAD.STEP_DATA',
    4: 'DAM.UPLOAD.STEP_REVIEW',
};

const ERROR_KEYS: Record<NonNullable<DamUploadFile['error']>, string> = {
    format: 'DAM.UPLOAD.ERROR_FORMAT',
    failed: 'DAM.UPLOAD.ERROR_FAILED',
    duplicate: 'DAM.UPLOAD.ERROR_DUPLICATE',
    create: 'DAM.UPLOAD.ERROR_CREATE',
};

/**
 * Upload wizard of the asset library (wireframe `7187:261798`, slot map
 * `dam-ui-rebuild-slot-map-upload.md`, D-046): "Step N of 4" — upload files, link products,
 * add asset data, review — then create. Files are staged in step 1; assets exist only after
 * "Create". Cancel or leaving the page with files asks before the staged uploads are discarded.
 */
function DamUploadWizard({ onClose, onCreated }: DamUploadWizardProps) {
    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { data: categories } = useAssetCategories();
    const upload = useDamUpload(categories?.[0]);
    const fields = useAssetOptionGroups(DAM_UPLOAD_LAYOUT);
    const linkedColumns = useDamLinkedItemColumns();
    const [step, setStep] = useState<DamUploadStep>(1);
    const [tab, setTab] = useState<DamUploadTab>('all');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [zoomed, setZoomed] = useState<DamUploadFile | undefined>(undefined);
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
    const [createProgress, setCreateProgress] = useState<{ done: number; total: number } | undefined>(undefined);

    const attributes = useMemo((): DataModelAttribute[] => (dataModel?.assetLayout(DAM_UPLOAD_LAYOUT)?.sections ?? [])
        .flatMap((section) => section.attributes ?? [])
        .filter((attribute) => !attribute.hidden)
        .map((attribute) => {
            const registered = dataModel?.attribute(attribute.name);
            return registered ? { ...registered, ...attribute } : attribute;
        }), [dataModel]);
    const mandatory = useMemo(
        () => attributes.filter((attribute) => attribute.params?.isMandatory === true).map((attribute) => attribute.name),
        [attributes],
    );

    const { files } = upload;
    const leaveGuard = useDamLeaveGuard(files.length > 0);
    const isDiscardDialogOpen = isConfirmingDiscard || leaveGuard.isAsking;
    const uploaded = files.filter(isUploaded);
    const failed = files.filter(isFailed);
    const isUploading = files.some((file) => file.status === 'uploading');
    const withoutProduct = uploaded.filter((file) => file.items.length === 0);
    const incomplete = uploaded.filter((file) => isIncomplete(file, mandatory));
    const withErrors = uploaded.filter(hasValidationErrors);
    const withWarnings = uploaded.filter(hasValidationWarnings);
    const selectedFiles = uploaded.filter((file) => selectedIds.has(file.id));

    const describeItem = (item: PersistedItem) => {
        const values = item as unknown as Record<string, unknown>;
        const parts = [linkedColumns.titleOf?.(item), ...linkedColumns.columns.map((column) => String(values[column.name] ?? ''))]
            .filter((part): part is string => Boolean(part) && part !== item.primaryKey__);
        return parts.join(' · ') || item.primaryKey__;
    };

    const goTo = (target: DamUploadStep, targetTab: DamUploadTab = 'all') => {
        setStep(target);
        setTab(targetTab);
        setSelectedIds(new Set());
        setIsDrawerOpen(false);
    };

    const requestClose = () => {
        if (files.length > 0 && createProgress === undefined) {
            setIsConfirmingDiscard(true);
        } else if (createProgress === undefined) {
            onClose();
        }
    };

    const keepFiles = () => {
        setIsConfirmingDiscard(false);
        leaveGuard.stay();
    };

    const changeSelection = (ids: string[], selected: boolean) => {
        const next = new Set(selectedIds);
        ids.forEach((id) => (selected ? next.add(id) : next.delete(id)));
        setSelectedIds(next);
        setIsDrawerOpen(next.size > 0);
    };

    const editOne = (file: DamUploadFile) => {
        setSelectedIds(new Set([file.id]));
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setSelectedIds(new Set());
        setIsDrawerOpen(false);
    };

    const runCreate = async () => {
        setCreateProgress({ done: 0, total: uploaded.length });
        const result = await upload.create((done, total) => setCreateProgress({ done, total }));
        setCreateProgress(undefined);
        if (result.created > 0) {
            onCreated(result);
        }
        if (result.duplicates + result.failed === 0) {
            onClose();
        } else {
            goTo(1, 'failed');
        }
    };

    const visibleFiles = (): DamUploadFile[] => {
        switch (tab) {
            case 'noProduct':
                return withoutProduct;
            case 'incomplete':
                return incomplete;
            case 'warnings':
                return withWarnings;
            case 'errors':
                return withErrors;
            default:
                return uploaded;
        }
    };

    const toUploadItem = (file: DamUploadFile): UploadItem => ({
        id: file.id,
        file: file.file,
        status: file.error ? 'error' : file.status,
        progress: file.progress,
        startedAt: file.startedAt,
        errorMessage: file.error ? t(ERROR_KEYS[file.error], { types: DAM_UPLOAD_FORMAT_LABELS }) : undefined,
    });

    const primary = (() => {
        switch (step) {
            case 1:
                return {
                    label: t('DAM.UPLOAD.NEXT_LINK'),
                    disabled: uploaded.length === 0 || isUploading,
                    onClick: () => goTo(2),
                };
            case 2:
                return { label: t('DAM.UPLOAD.NEXT_DATA'), disabled: uploaded.length === 0 || isDrawerOpen, onClick: () => goTo(3) };
            case 3:
                return { label: t('DAM.UPLOAD.NEXT_REVIEW'), disabled: uploaded.length === 0 || isDrawerOpen, onClick: () => goTo(4) };
            default:
                return {
                    label: uploaded.length === 1
                        ? t('DAM.UPLOAD.CREATE_ONE')
                        : t('DAM.UPLOAD.CREATE_MANY', { count: uploaded.length.toLocaleString() }),
                    disabled: uploaded.length === 0,
                    onClick: () => void runCreate(),
                };
        }
    })();

    const tabsFor = (current: DamUploadStep) => {
        if (current === 1) {
            return [
                { value: 'all' as const, label: t('DAM.UPLOAD.TAB_UPLOADED'), count: isUploading ? undefined : uploaded.length },
                { value: 'failed' as const, label: t('DAM.UPLOAD.TAB_FAILED'), count: isUploading ? undefined : failed.length },
            ];
        }
        if (current === 2) {
            return [
                { value: 'all' as const, label: t('DAM.UPLOAD.TAB_UPLOADED'), count: uploaded.length },
                { value: 'noProduct' as const, label: t('DAM.UPLOAD.TAB_NO_PRODUCT'), count: withoutProduct.length },
            ];
        }
        return [
            { value: 'all' as const, label: t('DAM.UPLOAD.TAB_UPLOADED'), count: uploaded.length },
            { value: 'incomplete' as const, label: t('DAM.UPLOAD.TAB_INCOMPLETE'), count: incomplete.length },
            { value: 'warnings' as const, label: t('DAM.UPLOAD.TAB_WARNINGS'), count: withWarnings.length },
            { value: 'errors' as const, label: t('DAM.UPLOAD.TAB_ERRORS'), count: withErrors.length },
        ];
    };

    const emptyTab = (
        <p className="tw-m-0 tw-rounded-lg tw-bg-fill-secondary tw-px-4 tw-py-6 tw-text-center tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
            {t('DAM.UPLOAD.TAB_EMPTY')}
        </p>
    );

    const renderStep = () => {
        if (step === 1) {
            const rows = tab === 'failed' ? failed : files;
            return (
                <div className="tw-flex tw-flex-col tw-gap-4">
                    {files.length > 0 && (
                        <DamUploadTabs tabs={tabsFor(1)} value={tab} onChange={setTab} label={t('DAM.UPLOAD.TABS')} />
                    )}
                    {tab === 'failed' && rows.length === 0 ? emptyTab : (
                        <Uploader
                            isMultiple
                            className="!tw-p-0"
                            uploads={rows.map(toUploadItem)}
                            onFilesAdded={upload.addFiles}
                            onFileRemove={(item) => upload.remove([item.id])}
                            onFileRetry={(item) => upload.retry(item.id)}
                        />
                    )}
                    <p className="tw-m-0 tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                        {t('DAM.UPLOAD.SUPPORTED_FORMATS', { formats: DAM_UPLOAD_FORMAT_LABELS })}
                    </p>
                </div>
            );
        }

        if (step === 4) {
            return (
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <DamUploadSummary
                        entries={[
                            { label: t('DAM.UPLOAD.SUMMARY_UPLOADED'), count: uploaded.length, linkLabel: t('DAM.UPLOAD.GO_TO_UPLOAD'), onOpen: () => goTo(1) },
                            {
                                label: t('DAM.UPLOAD.TAB_NO_PRODUCT'),
                                count: withoutProduct.length,
                                linkLabel: t('DAM.UPLOAD.GO_TO_FILES'),
                                onOpen: () => goTo(2, 'noProduct'),
                            },
                            {
                                label: t('DAM.UPLOAD.TAB_INCOMPLETE'),
                                count: incomplete.length,
                                linkLabel: t('DAM.UPLOAD.GO_TO_FILES'),
                                onOpen: () => goTo(3, 'incomplete'),
                            },
                            { label: t('DAM.UPLOAD.TAB_WARNINGS'), count: withWarnings.length, linkLabel: t('DAM.UPLOAD.GO_TO_FILES'), onOpen: () => goTo(3, 'warnings') },
                            { label: t('DAM.UPLOAD.TAB_ERRORS'), count: withErrors.length, linkLabel: t('DAM.UPLOAD.GO_TO_FILES'), onOpen: () => goTo(3, 'errors') },
                        ]}
                    />
                    <DamUploadFileList files={uploaded} mode="review" attributes={attributes} describeItem={describeItem} />
                </div>
            );
        }

        const rows = visibleFiles();
        return (
            <div className="tw-flex tw-flex-col tw-gap-4">
                <DamUploadTabs tabs={tabsFor(step)} value={tab} onChange={setTab} label={t('DAM.UPLOAD.TABS')} />
                {rows.length === 0 ? emptyTab : (
                    <DamUploadFileList
                        files={rows}
                        mode={step === 2 ? 'link' : 'data'}
                        attributes={attributes}
                        describeItem={describeItem}
                        selectedIds={selectedIds}
                        onSelectedChange={changeSelection}
                        onEdit={editOne}
                        onDelete={(file) => upload.remove([file.id])}
                        onZoom={setZoomed}
                    />
                )}
            </div>
        );
    };

    const zoomedUrl = useMemo(() => (zoomed ? URL.createObjectURL(zoomed.file) : undefined), [zoomed]);
    useEffect(() => () => {
        if (zoomedUrl) {
            URL.revokeObjectURL(zoomedUrl);
        }
    }, [zoomedUrl]);

    return (
        <>
            <Dialog open onOpenChange={(open) => !open && requestClose()}>
                <DialogContent variant="screen">
                    <DialogHeader>
                        <DialogHeaderDefault closeLabel={t('CLOSE')}>{t(STEP_TITLE_KEYS[step])}</DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody scrollable className="tw-relative">
                        {renderStep()}
                        {isDrawerOpen && selectedFiles.length > 0 && (step === 2 || step === 3) && (
                            <DamUploadDrawer
                                mode={step === 2 ? 'link' : 'data'}
                                files={selectedFiles}
                                fields={fields}
                                mandatory={mandatory}
                                describeItem={describeItem}
                                onConfirmLinks={(added, removed) => {
                                    upload.setItems([...selectedIds], (items) => [
                                        ...items.filter((item) => !removed.includes(item.primaryKey__)),
                                        ...added.filter((item) => !items.some((existing) => existing.primaryKey__ === item.primaryKey__)),
                                    ]);
                                    closeDrawer();
                                }}
                                onConfirmValues={(values) => {
                                    upload.setValues([...selectedIds], values);
                                    closeDrawer();
                                }}
                                onClose={closeDrawer}
                            />
                        )}
                    </DialogBody>
                    {files.length > 0 && (
                        <DialogFooter className="sm:tw-justify-between">
                            <Button
                                variant="outline"
                                hasIcon
                                icon={ArrowLeftIcon}
                                disabled={step === 1}
                                onClick={() => goTo((step - 1) as DamUploadStep)}
                            >
                                {t('DAM.UPLOAD.BACK')}
                            </Button>
                            <div className="tw-flex tw-gap-2">
                                <Button variant="outline" onClick={requestClose}>{t('CANCEL')}</Button>
                                <Button
                                    hasIcon={step < 4}
                                    icon={step < 4 ? ArrowRightIcon : undefined}
                                    disabled={primary.disabled}
                                    onClick={primary.onClick}
                                >
                                    {primary.label}
                                </Button>
                            </div>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
            {isDiscardDialogOpen && (
                <Dialog open onOpenChange={(open) => !open && keepFiles()}>
                    <DialogContent variant="small">
                        <DialogHeader>
                            <DialogHeaderDefault closeLabel={t('CLOSE')}>{t('DAM.UPLOAD.DISCARD_TITLE')}</DialogHeaderDefault>
                        </DialogHeader>
                        <DialogBody>
                            <p className="tw-m-0 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                {t('DAM.UPLOAD.DISCARD_TEXT', { count: files.length.toLocaleString() })}
                            </p>
                        </DialogBody>
                        <DialogFooter>
                            <Button variant="outline" onClick={keepFiles}>{t('CANCEL')}</Button>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    upload.discardAll();
                                    if (leaveGuard.isAsking) {
                                        leaveGuard.discard();
                                    } else {
                                        onClose();
                                    }
                                }}
                            >
                                {t('DAM.UPLOAD.DISCARD')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
            {createProgress && (
                <Dialog open>
                    <DialogContent variant="small" onEscapeKeyDown={(event) => event.preventDefault()} onInteractOutside={(event) => event.preventDefault()}>
                        <DialogBody className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-10 tw-text-center">
                            <Loader />
                            <p className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                {createProgress.total === 1
                                    ? t('DAM.UPLOAD.CREATE_ONE')
                                    : t('DAM.UPLOAD.CREATE_MANY', { count: createProgress.total.toLocaleString() })}
                            </p>
                            <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base" role="status">
                                {t('DAM.UPLOAD.CREATE_PROGRESS', { done: createProgress.done.toLocaleString(), count: createProgress.total.toLocaleString() })}
                            </p>
                            {withoutProduct.length > 0 && (
                                <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('DAM.UPLOAD.DEFECT_NO_PRODUCT', { count: withoutProduct.length.toLocaleString() })}
                                </p>
                            )}
                            {incomplete.length > 0 && (
                                <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('DAM.UPLOAD.DEFECT_INCOMPLETE', { count: incomplete.length.toLocaleString() })}
                                </p>
                            )}
                            {withErrors.length > 0 && (
                                <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    {t('DAM.UPLOAD.DEFECT_ERRORS', { count: withErrors.length.toLocaleString() })}
                                </p>
                            )}
                        </DialogBody>
                    </DialogContent>
                </Dialog>
            )}
            {zoomed && zoomedUrl && <DamImageLightbox imageUrl={zoomedUrl} title={zoomed.file.name} onClose={() => setZoomed(undefined)} />}
        </>
    );
}

export { DamUploadWizard };
export type { DamUploadWizardProps };
